import styled from '@emotion/styled'
import { css } from '@emotion/react'
import { stripUnit } from 'polished'

import { ColorDeterminer, WidthContainer } from '../../utils/helpers.styles'
import SectionComponent from '../Section'
import fluidRange from '../../utils/fluidRange'
import { getSectionPadding } from '../Section/Section.styles'

const contentAreaStyles = ({ theme, topPadding, bottomPadding }) => {
  const tp = getSectionPadding(theme.spacing, topPadding)
  const bp = getSectionPadding(theme.spacing, bottomPadding)

  return `
    margin: 0;
    width: 100%;

    @media screen and (min-width: ${theme.breakpoints.lg}) {
      display: flex;
      justify-content: center;
      flex-direction: column;
      width: 50%;
    }

    ${fluidRange('padding-top', [stripUnit(tp.mobile), stripUnit(tp.desktop)])};

    ${fluidRange('padding-bottom', [
      stripUnit(bp.mobile),
      stripUnit(bp.desktop),
    ])};
  `
}

export const MainContentArea = styled.div(
  contentAreaStyles,
  WidthContainer,
  ColorDeterminer
)

export const ModuleArea = styled.div(
  contentAreaStyles,
  ColorDeterminer,
  WidthContainer,
  ({ hasBackgroundImage }) => css`
    ${hasBackgroundImage && `position: relative;`}
  `
)

export const Section = styled(SectionComponent)(
  ({ theme, hasBackgroundColorOrImage, splitBackgroundColor }) => {
    // When no background color has been applied to section there's a lot of
    // extra spacing because there is no different background color to make the
    // distinction between the modules and the main content.
    const paddingSizes = hasBackgroundColorOrImage
      ? [theme.spacing.xl, theme.spacing.max]
      : [theme.spacing.md, theme.spacing.lg]
    // Generally a section has large left and right paddings to keep the content centered.
    // Since we are overriding the section padding we have to manually add the correct
    // padding to each content area to keep the content centered.
    const paddingLeft = fluidRange(
      'padding-left',
      paddingSizes.map((size) => stripUnit(size))
    )
    const paddingRight = fluidRange(
      'padding-right',
      paddingSizes.map((size) => stripUnit(size))
    )

    return css`
      display: flex;
      flex-direction: column;

      .leshen-package-card {
        .package-label {
          transform: translateY(3px);
        }
      }

      ${ModuleArea} {
        .leshen-stack {
          align-items: center;
        }
      }

      ${MainContentArea} {
        color: ${theme.colors.dark.base.hex};
        background-color: ${theme?.colors?.[splitBackgroundColor]?.base?.hex};
        // ||
        //   theme?.colors?.dark?.base?.hex

        .leshen-stack {
          gap: 16px;
          display: flex;
          flex-direction: column;

          > * {
            margin-top: 0;
            margin-bottom: 0;
          }

          h1,
          h2 {
            margin-bottom: 8px;
          }
        }
      }

      /* The content areas are handling their own padding since they have full background colors.
          The padding on sections was making it difficult to have a split background-color spanning
          the entire half of the section.
          We had to put padding in a media query since the section had higher specificity
          and we did not want to use an !important tag. */
      @media screen and (min-width: 0) {
        padding: 0;
      }

      @media screen and (min-width: ${theme.breakpoints.lg}) {
        flex-direction: row;
      }

      /* Every other section on the page has its conent reversed.
           Since areas are being reversed we also need to adjust the left and right padding
           to match the regular section container padding after being reversed. */

      &:nth-of-type(even) {
        @media screen and (min-width: ${theme.breakpoints.lg}) {
          flex-direction: row-reverse;
          ${MainContentArea} {
            ${paddingLeft};
          }

          ${ModuleArea} {
            ${paddingRight};
          }
        }
      }

      &:nth-of-type(odd) {
        @media screen and (min-width: ${theme.breakpoints.lg}) {
          ${ModuleArea} {
            ${paddingLeft};
          }

          ${MainContentArea} {
            ${paddingRight};
          }
        }
      }
    `
  }
)

export const BGImage = styled.div`
  display: block;
  position: absolute !important;

  top: 0;
  right: 0;
  bottom: 0;
  left: 0;

  // In decoupling from Gatsby image, i need to target the inline styles generated by gatsby on that div

  > .gatsby-image-wrapper {
    position: initial !important;

    // Gatsby Image has inline-styles with padding-bottom overflow into other sections.

    > div {
      padding-bottom: 0 !important;
    }

    picture {
      img {
        height: 100%;
      }
    }
  }

  > img {
    height: 100%;
    object-fit: cover;
    width: 100%;
  }
`

export const BackgroundImage = styled(BGImage)`
  ${(props) =>
    props.mobileBackgroundImage &&
    `
    @media (max-width: 539px) {
      display: none;
    }
  `}
`

export const MobileBackgroundImage = styled(BGImage)`
  @media (min-width: ${(props) => props.theme.breakpoints.sm}) {
    display: none;
  }
`

export const DesktopImg = styled.div`
  @media screen and (max-width: 939px) {
    display: none;
  }
`

export const MobileImg = styled.div`
  @media screen and (min-width: ${(props) => props.theme.breakpoints.lg}) {
    display: none;
  }
`
